<template>
  <div :style="cssProps">
    <v-toolbar dense density="compact" :color="$store.state.currentMoc.formColor" :elevation="8">
      <v-toolbar-title>{{ $t('Tasks') }}
        <v-tooltip bottom>
          <template v-slot:activator="{ props }" v-if="!addManualTaskDisabled">
            <v-btn icon @click="addManualTaskShow = true" v-bind="props">
              <v-icon>mdi-plus-circle-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('Add_Manual_Task') }}</span>
        </v-tooltip>

        <v-tooltip location="bottom" :text="showAllTasks ? $t('Hide') : $t('Show')"> 
          <template v-slot:activator="{ props }" v-if="!show">
            <v-btn icon @click="showAllTasks = !showAllTasks" v-bind="props">
              <v-icon>{{ showAllTasks ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
      </v-toolbar-title>

      <v-spacer></v-spacer>

      <v-menu :nudge-width="100" v-if="!isClosed">
        <template v-slot:activator="{ props }">
          
            <span>{{ displayFilterValue }} ({{ filteredTasks.length }})</span>
            <v-btn text icon v-bind="props">
              <v-icon dark>mdi-filter-variant</v-icon>
            </v-btn>
          
        </template>

        <v-list>          
            <v-list-item v-for="(item, index) in sortBy" :key="index" @click="filterBy(item)">
              <v-list-item-title v-text="item.value"></v-list-item-title>
            </v-list-item>          
        </v-list>
      </v-menu>

      <v-menu :nudge-width="100" v-if="!isClosed">
        <template v-slot:activator="{ props }">
          
            <span>{{ $t('Assigned') }}: {{ displayAssignedToByValue }}</span>
            <v-btn text icon v-bind="props">
              <v-icon dark>mdi-filter-variant</v-icon>
            </v-btn>
          
        </template>

        <v-list>
          <v-list-item v-for="(item, index) in assignedToBy" :key="index" @click="filterAssignedToBy(item)">
            <v-list-item-title v-text="item.value"></v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <csv-export :data="filteredTasks" :exportConfig="csvExportConfig"></csv-export>

    </v-toolbar>

    <v-data-table
      v-if="showAllTasks"
      hide-default-footer
      :headers="taskHeaders"
      :items="filteredTasks"
      item-value="id"
      density="compact"
      :items-per-page="-1"
      show-expand
      dense
      class="background-color"
      multi-sort
      :sort-by="[{ key: 'step', order: 'asc' }, { key: 'taskListType.value', order: 'asc' }]"
      :sort-desc="[false, false]"
      disable-pagination
    >      
      <template v-slot:item="{ item, internalItem, isExpanded, toggleExpand }">
        <tr class="rowone" :key="item.id + 'row1'">
          <td colspan="6" @click="toggleExpand(internalItem)" class="text-subtitle-1 titlerow task-no-border">
            <strong>{{ item.name }}</strong>
          </td>
        </tr>
        <tr class="rowtwo" :key="item.id + 'row2'">
          <td @click="toggleExpand(internalItem)" class="text-subtitle-1"></td>
          <td @click="toggleExpand(internalItem)" class="text-subtitle-1">{{ item.step }}</td>
          <td @click="toggleExpand(internalItem)" class="text-subtitle-1">{{ (item.assignedTo || {}).displayName }}</td>
          <td @click="toggleExpand(internalItem)" class="text-subtitle-1">{{ item.taskListType.value }}</td>
          <template v-if="taskIsEditable(item.taskListType.key)">
            <td class="text-subtitle-1">
              <v-badge
                :model-value="
                  successBadge[getIndexFromIdForSuccessBadge(item.taskId)] !== undefined
                    ? successBadge[getIndexFromIdForSuccessBadge(item.taskId)].status
                    : false
                "
                color="success"
                right
              >
                <template v-slot:badge>
                  <v-icon dark>mdi-check-outline</v-icon>
                </template>
                <v-select
                  dense
                  density="compact"
                  :model-value="item.status ? item.status.name : null"
                  :items="sortedTaskStatus(item.itemStatus)"
                  item-title="name"
                  item-value="name"
                  clearable
                  variant="underlined"
                  @click:clear.stop="clearStatus(item)"
                  @update:modelValue="handleTaskChange($event, item)"
                  :disabled="item.loading"
                  return-object
                  :readonly="readonly"
                  :error-messages="item.completed === true ? null : $t('Rule_Task_Must_Be_Satisfied')"
                ></v-select>
              </v-badge>
            </td>
          </template>
          <template v-else>
            <td @click="toggleExpand(internalItem)" class="text-subtitle-1">
              {{ item.status ? item.status.name : null }}
            </td>
          </template>
          <td @click="toggleExpand(internalItem)" class="text-subtitle-1">{{ $filters.DateFilter(item.completedDate) }}</td>
          <td class="pencilCell">
            <template v-if="taskIsEditable(item.taskListType.key) && !item.loading">
              <v-btn icon flat class="pencilCell">
                <v-icon color="accent" @click.stop="editTask(item)">mdi-pencil-outline</v-icon>
              </v-btn>
            </template>

            <template v-if="!taskIsEditable(item.taskListType.key) || item.loading">
              <v-icon color="error" @click="toggleExpand(internalItem)">mdi-cancel</v-icon>
            </template>
          </td>
        </tr>
        <tr class="rowthree" :key="item.id + 'row3'">
          <td colspan="6" style="padding-bottom: 20px">
            <template v-if="item.comment">
              <span class="commentLabel">{{ $t('Task_Comment') }}</span>
              {{ item.comment }}
            </template>
          </td>
        </tr>
      </template>      

      <template v-slot:expanded-row="{ columns, item }">
        <td :colspan="columns.length">
          <v-card class="white">
            <v-container fluid>
              <v-row>
                <v-col cols="12" lg="4" sm="5">
                  <peoplePicker :label="$t('Completed_By')" readonly :user="item.completedBy"></peoplePicker>
                </v-col>
                <v-col cols="12" lg="4" sm="5">
                  <peoplePicker :label="$t('Last_Modified_By')" readonly :user="item.modifiedBy"></peoplePicker>
                </v-col>
                <v-col cols="12" lg="3" sm="5">
                  <v-textarea
                    :label="$t('Modified_On')"
                    rows="1"
                    dense
                    readonly
                    variant="underlined"
                    :model-value="$filters.DateTimeFilter(item.modifiedByDate)"
                  ></v-textarea>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" sm="5">
                  <v-textarea :label="$t('Comments')" auto-grow rows="1" readonly variant="underlined" :model-value="item.comment" dense></v-textarea>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" sm="6">
                  <v-card>
                    <v-list dense>
                      <v-list-group>                        
                        <template v-slot:activator="{ props }">                          
                          <v-list-item v-bind="props">
                            <v-list-item-title>{{ $t('Supporting Links') }} ( {{ (item.supportingLinks || []).length }} )</v-list-item-title>
                          </v-list-item>
                        </template>
                        <v-list-item                          
                          v-for="(link, index) in item.supportingLinks"
                          :key="index"
                          @click.stop
                          :href="'//' + link.url"
                          target="_blank"
                        >
                          {{ link.name }}
                        </v-list-item>
                      </v-list-group>
                    </v-list>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-card>
                    <v-list dense>
                      <v-list-group>
                        <template v-slot:activator="{ props }">                          
                          <v-list-item v-bind="props">
                            <v-list-item-title>{{ $t('Standards') }} ( {{ (item.standards || []).length }} )</v-list-item-title>
                          </v-list-item>                          
                        </template>
                        <v-list-item
                          v-for="(standard, index) in item.standards"
                          :key="index"
                          @click.stop
                          :href="standard.url ? '//' + standard.url : '#'"
                          target="_blank"
                        >
                          {{ standard.name }}
                        </v-list-item>
                      </v-list-group>
                    </v-list>
                  </v-card>
                </v-col>
              </v-row>
              
              <v-row>
                <v-col cols="12">
                  <task-history :history="item.history"></task-history>
                </v-col>
              </v-row>

              <template v-if="item.addedVia === 'manual'">
                <v-row justify="end" class="mr-2">
                  <span>
                    {{ $t('Manual_Task_Added_By') }}: {{ item.insertedBy.displayName }} -
                    {{ $filters.DateTimeFilter(item.insertedByDate) }}
                  </span>
                </v-row>
              </template>
            </v-container>
          </v-card>
        </td>
      </template>
    </v-data-table>

    <template v-if="addManualTaskShow">
      <Add-Manual-Task
        :mocId="moc.mocId"
        :mocStatus="moc.status"
        @close="handleAddManualTaskClose()"
        @outTask="handleOutTask($event)"
      ></Add-Manual-Task>
    </template>

    <toaster ref="toaster"></toaster>

    <v-dialog v-model="dialog" max-width="1200" persistent>
      <v-card>
        
        <task
          :task="editedTask"
          @taskAssignedTo="setTaskAssignee($event, editTask)"
          @completed="$emit('taskStatusChange', editedTask.completed)"
        ></task>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="info" class="text-button-black text-white" @click="cancel(editedTask)">{{ $t('Cancel') }}</v-btn>
          <v-btn color="success" @click="saveTask(editedTask)">{{ $t('Save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <loading-dialog ref="loadingDialog"></loading-dialog>
  </div>
</template>

<script>
import { FormStatus, Roles } from '@/common'
import { Flags } from '@/common/flags'
import MOCService from '@/services/moc.service'
import { ChangeTypes, TaskListTypes } from '@/common/types'
import Task from '@/components/task/Task'
import { mapState } from 'vuex'
import { multiPropertySort } from '@/common/arrayFunctions'
import TaskService from '@/services/task.service'
import Toaster from '@/components/Toaster'
import PeoplePicker from '@/components/peoplePicker/PeoplePicker'
import CsvExport from '@/components/CsvExport'
import TaskHistory from '@/components/task/TaskHistory'
import AddManualTask from '@/components/task/AddManualTask'
import LoadingDialog from '@/components/mocForm/LoadingDialog'
export default {
  data() {
    return {
      expanded: [],
      singleExpand: true,
      editedTask: {
        task: ''
      },
      dialog: false,
      filter: null,
      assignedToFilter: null,
      taskHeaders: [
        { title: this.$t('Task'), key : 'name' },
        { title: this.$t('Step'), key : 'step' },
        { title: this.$t('Assigned_To'), key : 'assignedTo.displayName' },
        { title: this.$t('Required_In_Order_To'), key : 'taskListType.value' },
        { title: this.$t('Status'), key : 'status.name' },
        { title: this.$t('Completed_Date'), key : 'completedDate' },
        { title: this.$t('Edit'), key : 'data-table-expand', sortable: false }
      ],
      mocStatusVal: 100,
      holdTask: {},
      csvExportConfig: {
        [this.$t('Task')]: 'name',
        [this.$t('Step')]: 'step',
        [this.$t('Assigned_To')]: 'assignedTo.displayName',
        [this.$t('Required_In_Order_To')]: 'taskListType.value',
        [this.$t('Status')]: 'status.name',
        [this.$t('Completed_Date')]: {
          property: 'completedDate',
          isDate: true
        }
      },        
      addManualTaskShow: false,
      successBadge: [],
      showAllTasks: this.show
    }
  },
  props: {
    moc: {
      type: Object
    },
    tasks: {
      type: Array
    },
    readonly: {
      type: Boolean
    },
    roles: {
      type: Array,
      default: null
    },
    previousFormStatus: {
      type: Object
    },
    show: {
      type: Boolean,
      default: true
    }
  },
  components: {
    Task,
    Toaster,
    PeoplePicker,
    CsvExport,
    TaskHistory,
    AddManualTask,
    LoadingDialog
  },
  methods: {
    cancel: function (task) {
      task.status = this.holdTask.status

      task.comment = this.holdTask.comment
      task.completed = this.holdTask.completed
      task.completedBy = this.holdTask.completedBy
      task.completedDate = this.holdTask.completedDate
      task.assignedTo = this.holdTask.assignedTo !== null ? this.holdTask.assignedTo : {}

      this.dialog = false
      this.holdTask = {}
    },
    clearStatus: function (task) {
      this.setCompletedDate(task, null)
      task.completed = false
      task.status = { name: null, value: null }
      task.completedBy = null
      this.$emit('taskStatusChange', false)
      this.saveTask(task)
    },
    clicked(value) {
      this.expanded.push(value)
    },
    editTask(task) {
      this.holdTask = { ...task }
      this.editedTask = task
      this.dialog = true
    },
    filterBy: function (item) {
      this.filter = item.key
    },
    filterAssignedToBy: function (val) {
      this.assignedToFilter = val
    },
    fetchMocStatus: function () {
      MOCService.getMocStatus(this.moc.mocId)
        .then((result) => {
          this.formStatusValue(result)
        })
        .catch((e) => {
          this.$refs.toaster.pop('error', 'fetchMocStatus: ' + e)
        })
    },
    formStatusValue: function (val) {
      switch (val.value) {
        case FormStatus.properties[FormStatus.NEW].value:
        case FormStatus.properties[FormStatus.DRAFT].value:
        case FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value:
        case FormStatus.properties[FormStatus.BEINGREVISED].value:
          this.mocStatusVal = 1
          break
        case FormStatus.properties[FormStatus.SUBMITTED].value:
          this.mocStatusVal = 2
          break
        case FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value:
          this.mocStatusVal = 3
          break
        case FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value:
          this.mocStatusVal = 4
          break
        case FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value:
          this.mocStatusVal = 5
          break
        case FormStatus.properties[FormStatus.CHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value:
        case FormStatus.properties[FormStatus.TEMPORARYCHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value:
          this.mocStatusVal = 6
          break
      }
    },
    getIndexFromId: function (id) {
      return this.tasks
        .map(function (t) {
          return t.taskId
        })
        .indexOf(id)
    },
    getIndexFromIdForSuccessBadge: function (id) {
      return this.successBadge
        .map(function (t) {
          return t.id
        })
        .indexOf(id)
    },
    handleAddManualTaskClose: function () {
      this.addManualTaskShow = false
    },
    handleOutTask: function (task) {
      this.tasks.push(task)
    },
    handleTaskChange: async function (selectedItem, task) {
      if (task.loading) {
        return
      }
      if (!selectedItem) {
        return
      }

      if (task.status && selectedItem.name === task.status.name) {
        return
      }

      task.loading = true
      let completed = false

      if (selectedItem.required) {
        completed = true
        task.completedBy = this.user
        this.setCompletedDate(task, new Date())
      }

      if (!selectedItem.required) {
        completed = false
        task.completedBy = null
        this.setCompletedDate(task, null)
      }

      task.status = { name: selectedItem.name, value: completed ? 'Completed' : null }
      task.completed = completed
      this.$emit('taskStatusChange', completed)

      await this.saveTask(task)
      task.loading = false
    },
    loadSuccess: function () {
      if (this.tasks.length > 0) {
        //TODO: consider if we really need to clear these arrays or not
        this.tasks.forEach((i) => {
          i.loading = false
        })
        this.loading = false
      }
    },
    populateSuccessBadge: function () {
      this.tasks.forEach((t) => {
        let existingTaskId = this.successBadge.filter((sb) => {
          return t.taskId === sb.id
        })

        if (existingTaskId.length === 0) {
          this.successBadge.push({
            id: t.taskId,
            status: false
          })
        }
      })
    },
    sleep: async function (msec) {
      return new Promise((resolve) => setTimeout(resolve, msec))
    },
    saveTask: async function (task) {
      this.$refs.loadingDialog.show()
      await TaskService.saveSingleTask(task)
        .then((data) => {
          if (
            task.assignedTo &&
            this.holdTask.id &&
            (this.holdTask.assignedTo || {}).id !== task.assignedTo.id &&
            FormStatus.properties[FormStatus.NEW].value !== this.moc.status.value &&
            !this.isClosed &&
            !task.completed
          ) {
            MOCService.notifyTaskAssignment(this.moc.mocId, task).catch((e) => console.error(e))
          }
          let taskIndex = this.getIndexFromId(task.taskId)
          let statusIndex = this.getIndexFromIdForSuccessBadge(task.taskId)

          this.tasks.splice(taskIndex, 1, data.item2)
          this.successBadge[statusIndex].status = true
          this.$forceUpdate()

          setTimeout(() => {
            this.successBadge[statusIndex].status = false
            this.$forceUpdate()
          }, 2000)
          this.dialog = false
          this.holdTask = {}
          this.$refs.loadingDialog.hide()
        })
        .catch((e) => {
          this.$refs.loadingDialog.error({
            error: e,
            metadata: 'saveTask',
            message: this.$t('Save_Single_Task_Failed')
          })
        })
    },
    setCompletedDate: function (task, date) {
      task.completedDate = date
    },
    setCurrentTasklistType: function () {
      this.selectedTasklistType = 2
      this.filter = 2
    },
    setTaskAssignee: function (assignee) {
      if (assignee) {
        this.editedTask.assignedTo = assignee
      }
    },
    sortedTaskStatus: function (arrItemStatus) {
      let holdItemStatus = arrItemStatus.slice()
      let sortBy = [{ prop: 'displayOrder', direction: 1 }]
      if (holdItemStatus) {
        let sorted = multiPropertySort(sortBy, holdItemStatus)
        return sorted
      }
      return null
    },
    taskIsEditable: function (taskListTypeKey) {
      if (this.isClosed || this.readonly) {
        return false
      }

      // this is a special case where the MOC is Temp and in Make Perm but it has expired
      // this will allow the coordinator to moc the MOC forward
      if (
        (this.moc.flags || []).includes(Flags.properties[Flags.MAKEPERMANENT].key) &&
        (this.moc.flags || []).includes(Flags.properties[Flags.UNAUTHORIZED].key) &&
        this.moc.status.value === FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value &&
        this.moc.changeType.type.key === ChangeTypes.properties[ChangeTypes.TEMPORARY].key &&
        taskListTypeKey === TaskListTypes.properties[TaskListTypes.CONFIRMCHANGEISINPLACE].key
      ) {
        return true
      }
      if (
        (this.moc.flags || []).includes(Flags.properties[Flags.MAKEPERMANENT].key) &&
        (this.moc.flags || []).includes(Flags.properties[Flags.UNAUTHORIZED].key) &&
        this.moc.status.value === FormStatus.properties[FormStatus.TEMPORARYCHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value &&
        this.moc.changeType.type.key === ChangeTypes.properties[ChangeTypes.TEMPORARY].key &&
        taskListTypeKey === TaskListTypes.properties[TaskListTypes.CONFIRMCHANGEISFULLYIMPLEMENTED].key
      ) {
        return true
      }
      if (
        (this.moc.flags || []).includes(Flags.properties[Flags.RETURNTOORIGINALCONDITION].key) &&
        (this.moc.flags || []).includes(Flags.properties[Flags.UNAUTHORIZED].key) &&
        this.moc.status.value === FormStatus.properties[FormStatus.TEMPORARYCHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value &&
        this.moc.changeType.type.key === ChangeTypes.properties[ChangeTypes.TEMPORARY].key &&
        taskListTypeKey === TaskListTypes.properties[TaskListTypes.CONFIRMCHANGEISFULLYIMPLEMENTED].key
      ) {
        return true
      }

      if (
        (this.moc.flags || []).includes(Flags.properties[Flags.RETURNTOORIGINALCONDITION].key) &&
        this.moc.status.value === FormStatus.properties[FormStatus.CHANGEISFULLYIMPLEMENTED].value
      ) {
        return false
      }

      if ((this.moc.flags || []).includes(Flags.properties[Flags.UNAUTHORIZED].key)) {
        return false
      }

      let taskVal = 100
      switch (taskListTypeKey) {
        case TaskListTypes.properties[TaskListTypes.ALL].key:
          taskVal = 0
          break
        case TaskListTypes.properties[TaskListTypes.SUBMIT].key:
          taskVal = 1
          break
        case TaskListTypes.properties[TaskListTypes.SENDFORDECISION].key:
          taskVal = 2
          break
        case TaskListTypes.properties[TaskListTypes.ENABLEOWNERDECISIONTOOCCUR].key:
          taskVal = 3
          break
        case TaskListTypes.properties[TaskListTypes.PROCEEDWITHIMPLEMENTATION].key:
          taskVal = 4
          break
        case TaskListTypes.properties[TaskListTypes.CONFIRMCHANGEISINPLACE].key:
          taskVal = 5
          break
        case TaskListTypes.properties[TaskListTypes.CONFIRMCHANGEISFULLYIMPLEMENTED].key:
          taskVal = 6
          break
      }
      return this.mocStatusVal === taskVal ? true : false
    },
    taskListTypeToString: function (typeKey) {
      return TaskListTypes.properties[typeKey].value
    }
  },
  computed: {
    ...mapState('currentUser', ['user']),
    ...mapState('currentMoc', ['formColor']),
    sortBy: function () {
      let arr = Object.keys(TaskListTypes.properties).map((k) => TaskListTypes.properties[k])
      return arr
    },
    addManualTaskDisabled: function () {
      return (
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDREJECTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDVOIDED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDSTRANDED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CHANGEISFULLYIMPLEMENTED].value
      )
    },
    assignedToBy: function () {
      return [
        { key: TaskListTypes.properties[TaskListTypes.ALL].key, value: TaskListTypes.properties[TaskListTypes.ALL].value },
        { key: 'justme', value: 'Just me' }
      ]
    },
    cssProps() {
      switch (this.$store.state.currentMoc.formColor) {
        case 'backgroundNormal': {
          return {
            '--firstRowEven': '#E3F2FD',
            '--firstRowOdd': '#bbdefb',
            '--secondRowOdd': '#E3F2FD',
            '--secondRowEven': '#bbdefb',
            '--thirdRowOdd': '#bbdefb',
            '--thirdRowEven': '#E3F2FD',
            '--bgcolor': '#E3F2FD'
          }
        }
        case 'backgroundUnauthorizedExpired': {
          return {
            '--firstRowEven': '#FBE9E7',
            '--firstRowOdd': '#ECDAD8',
            '--secondRowOdd': '#FBE9E7',
            '--secondRowEven': '#ECDAD8',
            '--thirdRowOdd': '#ECDAD8',
            '--thirdRowEven': '#FBE9E7',
            '--bgcolor': '#FBE9E7'
          }
        }
        case 'backgroundUnauthorizedExpired2': {
          return {
            '--firstRowEven': '#FFEBEE',
            '--firstRowOdd': '#F0DCDF',
            '--secondRowEven': '#F0DCDF',
            '--thirdRowOdd': '#F0DCDF',
            '--bgcolor': '#FFEBEE'
          }
        }
        case 'backgroundUnauthorizedExpired3': {
          return {
            '--firstRowEven': '#F3E5F5',
            '--firstRowOdd': '#DED8E7',
            '--secondRowEven': '#DED8E7',
            '--thirdRowOdd': '#DED8E7',
            '--bgcolor': '#F3E5F5'
          }
        }
        default:
          return {
            '--firstRowEven': '#E3F2FD',
            '--firstRowOdd': '#bbdefb',
            '--secondRowEven': '#bbdefb',
            '--thirdRowOdd': '#bbdefb',
            '--bgcolor': '#E3F2FD'
          }
      }
    },
    displayAssignedToByValue: function () {
      if (this.assignedToFilter) {
        return this.assignedToFilter.value
      }
      return TaskListTypes.properties[TaskListTypes.ALL].value
    },
    defaultFilter: function () {
      this.loadSuccess()
      switch (this.moc.status.value) {
        case FormStatus.properties[FormStatus.NEW].value:
        case FormStatus.properties[FormStatus.DRAFT].value:
        case FormStatus.properties[FormStatus.BEINGREVISED].value:
        case FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value: {
          return { key: TaskListTypes.SUBMIT }
        }
        case FormStatus.properties[FormStatus.SUBMITTED].value: {
          return { key: TaskListTypes.SENDFORDECISION }
        }
        case FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value: {
          return { key: TaskListTypes.ENABLEOWNERDECISIONTOOCCUR }
        }
        case FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value: {
          return { key: TaskListTypes.PROCEEDWITHIMPLEMENTATION }
        }
        case FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value: {
          return { key: TaskListTypes.CONFIRMCHANGEISINPLACE }
        }
        case FormStatus.properties[FormStatus.CHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value:
        case FormStatus.properties[FormStatus.CHANGEISFULLYIMPLEMENTED].value:
        case FormStatus.properties[FormStatus.TEMPORARYCHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value: {
          return { key: TaskListTypes.CONFIRMCHANGEISFULLYIMPLEMENTED }
        }
      }
      return { key: TaskListTypes.ALL }
    },
    displayFilterValue: function () {
      if (this.filter) {
        return TaskListTypes.properties[this.filter].value
      }
      return TaskListTypes.properties[TaskListTypes.ALL].value
    },
    filteredTasks: function () {
      this.loadSuccess()
      if (this.filter) {
        switch ((this.assignedToFilter || {}).key) {
          case 'justme':
            return this.tasks.filter((t) => {
              return (
                t.taskListType.key === TaskListTypes.properties[this.filter].key &&
                t.assignedTo &&
                t.assignedTo.id === this.user.id
              )
            })
          default:
            if (!this.isClosed) {
              if (TaskListTypes.properties[this.filter].key === TaskListTypes.properties[TaskListTypes.ALL].key) {
                return this.tasks
              } else {
                return this.tasks.filter((t) => t.taskListType.key === TaskListTypes.properties[this.filter].key)
              }
            }
        }
      } else {
        if (this.assignedToFilter) {
          switch (this.assignedToFilter.key) {
            case 'justme':
              return this.tasks.filter((t) => {
                return (t.assignedTo || {}).id === this.user.id
              })
            default:
              return this.tasks
          }
        }
      }
      return this.tasks
    },
    isClosed: function () {
      return (
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDVOIDED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDREJECTED].value ||
        this.moc.status.value === FormStatus.properties[FormStatus.CLOSEDSTRANDED].value
      )
    }
  },
  mounted() {
    this.fetchMocStatus()
    this.filterBy(this.defaultFilter)
    this.$emit('loadFilteredTasksInCurrentStatus', this.filteredTasks)
  },
  beforeUpdate() {
    this.populateSuccessBadge()
    this.$emit('loadFilteredTasksInCurrentStatus', this.filteredTasks)
  }
}
</script>

<style scoped>
.v-data-table tbody tr:not(:last-child) td:last-child {
  border-bottom: 0px solid rgba(255, 255, 255, 0.12);
}

.commentLabel {
  font-weight: bold;
}

.pencilCell {
  background-color: var(--bgcolor);
}

.background-color {
  background-color: var(--bgcolor);
}

.rowone {
  background-color: var(--firstRowOdd);
}

.rowone:nth-of-type(odd) {
  background-color: var(--firstRowOdd);
}

.rowone:nth-of-type(even) {
  background-color: var(--firstRowEven);
}
.rowtwo:nth-of-type(odd) {
  background-color: var(--secondRowOdd);
}
.rowtwo:nth-of-type(even) {
  background-color: var(--secondRowEven);
}
.rowthree:nth-of-type(odd) {
  background-color: var(--thirdRowOdd);
}
.rowthree:nth-of-type(even) {
  background-color: var(--thirdRowEven);
}
.task-no-border{
  border:0 !important;
}
.rowone:hover {
  background-color: whitesmoke;
}
.rowtwo:hover {
  background-color: whitesmoke;
}
.rowthree:hover {
  background-color: whitesmoke;
}
.text-button-black { 
   background-color: black !important;   
}
.text-white{
  color:white !important;
}
</style>
